import type { ReactElement } from "react";
import type { LayoutKatalog } from "@kundeninfo/api-client";
import { EinheitFeld, FarbFeld, SchalterFeld, WahlFeld, feldId } from "./felder.js";
import { mitWert, wertAn } from "../layoutEntwurf.js";
import type { PanelProps } from "../LayoutRegister.js";
import { SECTION_TEXT } from "../layoutTexts.js";

type Sektion = LayoutKatalog["sektionen"][number];

/** Register „Sektionen": je Sektion des Katalogs ohne eigene Gruppe ihre Felder (`SektionFelder`). Die Sektionen der
 *  Gruppe `positionstabelle` (Überschriften, Bezeichnungen) stehen im Register „Tabelle". */
export function SektionenPanel(p: PanelProps): ReactElement {
  return (
    <div className="lay__panel">
      <p className="lay__panelhinweis">{SECTION_TEXT.hint}</p>
      {p.stand.katalog.sektionen.filter((s) => s.gruppe === undefined)
        .map((s) => <SektionFelder key={s.id} {...p} sektion={s} />)}
    </div>
  );
}

/** Eine Wahl zwischen zwei Schnitten mit „Systemwert" — `fett` (fett/normal) und `kursiv` (kursiv/aufrecht). */
function SchnittWahl(p: PanelProps & { sektion: Sektion; feld: "fett" | "kursiv" }): ReactElement {
  const { sektion: s, feld } = p;
  const [an, aus, label] = feld === "fett"
    ? [SECTION_TEXT.bold, SECTION_TEXT.normal, SECTION_TEXT.weight(s.label)]
    : [SECTION_TEXT.italic, SECTION_TEXT.upright, SECTION_TEXT.style(s.label)];
  const pfad = ["sektionen", s.id, feld];
  const wert = wertAn(p.entwurf, pfad);
  return (
    <WahlFeld id={feldId(pfad.join("."))} label={label}
      wert={typeof wert === "boolean" ? (wert ? an : aus) : ""}
      optionen={[{ wert: "", text: SECTION_TEXT.systemValue(s[feld] === true ? an : aus) },
                 { wert: an, text: an }, { wert: aus, text: aus }]}
      fehler={p.feldFehler[pfad.join(".")]}
      onWahl={(w) => {
        p.onEntwurf(mitWert(p.entwurf, pfad, w === "" ? undefined : w === an));

        p.onFeldVerlassen();
      }} />
  );
}

/**
 * Die Felder EINER Sektion — genau die, die ihr Katalogeintrag trägt: Größe in pt (rechtsbündig mit Einheit), Schnitt
 * (fett), Stil (kursiv), Farbe (Farbfeld, Systemwert Akzent- bzw. Textfarbe) und am Hinweis der Akzentbalken.
 */
export function SektionFelder(p: PanelProps & { sektion: Sektion }): ReactElement {
  const s = p.sektion;
  const pfad = `sektionen.${s.id}`;
  const setze = (feld: string, neu: unknown) => p.onEntwurf(mitWert(p.entwurf, ["sektionen", s.id, feld], neu));
  const akzent = wertAn(p.entwurf, ["farben", "akzent"]);
  return (
    <fieldset className="lay__gruppe">
      <legend>{s.label}</legend>
      {s.groesse !== undefined && (
        <EinheitFeld id={feldId(`${pfad}.groesse`)} label={SECTION_TEXT.size(s.label)} einheit="pt"
          wert={wertAn(p.entwurf, ["sektionen", s.id, "groesse"])} systemwert={s.groesse}
          fehler={p.feldFehler[`${pfad}.groesse`]} onWert={(neu) => setze("groesse", neu)}
          onVerlassen={p.onFeldVerlassen} />
      )}
      <SchnittWahl {...p} feld="fett" />
      {s.kursiv !== undefined && <SchnittWahl {...p} feld="kursiv" />}
      {s.farbe !== undefined && (
        <FarbFeld id={feldId(`${pfad}.farbe`)} label={SECTION_TEXT.color(s.label)}
          wert={wertAn(p.entwurf, ["sektionen", s.id, "farbe"])} systemwert={SECTION_TEXT.systemColor[s.farbe]}
          palette={[{ hex: typeof akzent === "string" ? akzent : p.stand.unternehmen.colorHex,
                      name: SECTION_TEXT.systemColor.akzent }].filter((f) => /^#[0-9A-Fa-f]{6}$/.test(f.hex))}
          fehler={p.feldFehler[`${pfad}.farbe`]} onWert={(neu) => setze("farbe", neu)}
          onVerlassen={p.onFeldVerlassen} />
      )}
      {s.balken !== undefined && (
        <SchalterFeld id={feldId(`${pfad}.balken`)} label={SECTION_TEXT.bar}
          wert={wertAn(p.entwurf, ["sektionen", s.id, "balken"])} systemwert={s.balken}
          fehler={p.feldFehler[`${pfad}.balken`]}
          onWert={(neu) => { setze("balken", neu); p.onFeldVerlassen(); }} />
      )}
    </fieldset>
  );
}
